import { PixelBuffer } from "../../core/pixel/PixelBuffer";

// Decode a Blob (PNG, JPEG, WebP, AVIF, SVG, etc. browser-supported) into a
// PixelBuffer. Browser-side decoder: lives in app/ because it depends on
// the DOM (createImageBitmap + 2D canvas). Format-specific pure parsers
// (PSD, AI, Sketch, ...) belong in core/io when they exist.

export async function decodeImage(file: Blob): Promise<{
  pixels: PixelBuffer;
  width: number;
  height: number;
}> {
  const bitmap = await createImageBitmap(file);
  try {
    const off = document.createElement("canvas");
    off.width = bitmap.width;
    off.height = bitmap.height;
    const ctx = off.getContext("2d");
    if (!ctx) throw new Error("2D context unavailable for decode");
    ctx.drawImage(bitmap, 0, 0);
    const imageData = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
    const buf = new PixelBuffer(bitmap.width, bitmap.height, "rgba8");
    buf.data.set(imageData.data);
    return { pixels: buf, width: bitmap.width, height: bitmap.height };
  } finally {
    bitmap.close();
  }
}

export async function decodeSvg(file: Blob, targetWidth?: number, targetHeight?: number): Promise<{
  pixels: PixelBuffer;
  width: number;
  height: number;
}> {
  const bitmap = await createImageBitmap(file);
  try {
    let w = bitmap.width;
    let h = bitmap.height;
    if (!w || !h) {
      w = targetWidth ?? 1920;
      h = targetHeight ?? 1080;
    }
    const off = document.createElement("canvas");
    off.width = w;
    off.height = h;
    const ctx = off.getContext("2d");
    if (!ctx) throw new Error("2D context unavailable for decode");
    ctx.drawImage(bitmap, 0, 0);
    const imageData = ctx.getImageData(0, 0, w, h);
    const buf = new PixelBuffer(w, h, "rgba8");
    buf.data.set(imageData.data);
    return { pixels: buf, width: w, height: h };
  } finally {
    bitmap.close();
  }
}

export async function decodeBmp(file: Blob): Promise<{
  pixels: PixelBuffer;
  width: number;
  height: number;
}> {
  const bitmap = await createImageBitmap(file);
  try {
    const off = document.createElement("canvas");
    off.width = bitmap.width;
    off.height = bitmap.height;
    const ctx = off.getContext("2d");
    if (!ctx) throw new Error("2D context unavailable for decode");
    ctx.drawImage(bitmap, 0, 0);
    const imageData = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
    const buf = new PixelBuffer(bitmap.width, bitmap.height, "rgba8");
    buf.data.set(imageData.data);
    return { pixels: buf, width: bitmap.width, height: bitmap.height };
  } finally {
    bitmap.close();
  }
}

// GIF decoder — uses browser's built-in decoder. Returns the first frame.
export async function decodeGif(file: Blob): Promise<{
  pixels: PixelBuffer;
  width: number;
  height: number;
}> {
  return decodeImage(file);
}

// ICO decoder — uses browser's built-in decoder. Returns the largest/best icon.
export async function decodeIco(file: Blob): Promise<{
  pixels: PixelBuffer;
  width: number;
  height: number;
}> {
  return decodeImage(file);
}

// BMP writer — writes 24-bit BMP from a PixelBuffer.
export function encodeBmp(pixels: PixelBuffer): Blob {
  const w = pixels.width;
  const h = pixels.height;
  const rowSize = Math.ceil((w * 3) / 4) * 4;
  const pixelDataSize = rowSize * h;
  const fileSize = 54 + pixelDataSize;

  const buffer = new ArrayBuffer(fileSize);
  const view = new DataView(buffer);
  const data = pixels.data;

  // BMP file header (14 bytes)
  view.setUint8(0, 0x42); // 'B'
  view.setUint8(1, 0x4d); // 'M'
  view.setUint32(2, fileSize, true);
  view.setUint16(6, 0, true); // reserved
  view.setUint16(8, 0, true); // reserved
  view.setUint32(10, 54, true); // pixel data offset

  // DIB header (BITMAPINFOHEADER, 40 bytes)
  view.setUint32(14, 40, true); // header size
  view.setInt32(18, w, true);
  view.setInt32(22, h, true); // positive = bottom-up
  view.setUint16(26, 1, true); // color planes
  view.setUint16(28, 24, true); // bits per pixel
  view.setUint32(30, 0, true); // no compression
  view.setUint32(34, pixelDataSize, true);
  view.setInt32(38, 2835, true); // ~72 DPI horizontal
  view.setInt32(42, 2835, true); // ~72 DPI vertical
  view.setUint32(46, 0, true); // colors used
  view.setUint32(50, 0, true); // important colors

  // Pixel data (bottom-up, BGR)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const srcIdx = ((h - 1 - y) * w + x) * 4;
      const dstIdx = 54 + y * rowSize + x * 3;
      view.setUint8(dstIdx, data[srcIdx + 2]!); // B
      view.setUint8(dstIdx + 1, data[srcIdx + 1]!); // G
      view.setUint8(dstIdx + 2, data[srcIdx]!); // R
    }
  }

  return new Blob([buffer], { type: "image/bmp" });
}

// ICO writer — writes a simple 256x256 32-bit ICO from a PixelBuffer.
export function encodeIco(pixels: PixelBuffer): Blob {
  const w = Math.min(pixels.width, 256);
  const h = Math.min(pixels.height, 256);
  const wByte = w === 256 ? 0 : w;
  const hByte = h === 256 ? 0 : h;

  const pixelDataSize = w * h * 4;
  const maskSize = Math.ceil(w / 32) * 4 * h;
  const totalSize = 6 + 16 + pixelDataSize + maskSize;

  const buffer = new ArrayBuffer(totalSize);
  const view = new DataView(buffer);
  const out = new Uint8Array(buffer);
  const data = pixels.data;

  // ICO header (6 bytes)
  view.setUint16(0, 0, true); // reserved
  view.setUint16(2, 1, true); // type: 1 = icon
  view.setUint16(4, 1, true); // number of images

  // ICO entry (16 bytes)
  out[6] = wByte;
  out[7] = hByte;
  out[8] = 0; // color palette
  out[9] = 0; // reserved
  view.setUint16(10, 1, true); // color planes
  view.setUint16(12, 32, true); // bits per pixel
  view.setUint32(14, pixelDataSize + maskSize, true); // size
  view.setUint32(18, 22, true); // offset to pixel data

  // PNG-compressed image data would be ideal, but for simplicity we write raw RGBA
  // (bottom-up, with AND mask after)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const srcIdx = ((h - 1 - y) * w + x) * 4;
      const dstIdx = 22 + (y * w + x) * 4;
      out[dstIdx] = data[srcIdx]!; // R
      out[dstIdx + 1] = data[srcIdx + 1]!; // G
      out[dstIdx + 2] = data[srcIdx + 2]!; // B
      out[dstIdx + 3] = data[srcIdx + 3]!; // A
    }
  }

  // AND mask (1 bit per pixel, padded to 32-bit rows)
  const maskRowBytes = Math.ceil(w / 32) * 4;
  const maskOffset = 22 + pixelDataSize;
  for (let y = 0; y < h; y++) {
    for (let byteIdx = 0; byteIdx < maskRowBytes; byteIdx++) {
      out[maskOffset + y * maskRowBytes + byteIdx] = 0; // all visible
    }
  }

  return new Blob([buffer], { type: "image/x-icon" });
}

// TIFF reader — basic, no compression support. Reads RGB and RGBA.
export async function decodeTiff(file: Blob): Promise<{
  pixels: PixelBuffer;
  width: number;
  height: number;
}> {
  const buffer = await file.arrayBuffer();
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);

  // Read byte order
  const byteOrder = view.getUint16(0, false);
  const littleEndian = byteOrder === 0x4949; // "II"

  // Read IFD offset
  const ifdOffset = view.getUint32(4, littleEndian);

  // Read number of directory entries
  const numEntries = view.getUint16(ifdOffset, littleEndian);

  // Parse tags
  let width = 0, height = 0, bitsPerSample = 8, samplesPerPixel = 3;
  let compression = 1; // 1 = no compression
  let stripOffset = 0, stripByteCount = 0;
  let photometricInterpretation = 2; // 2 = RGB, 3 = palette, 6 = YCbCr

  for (let i = 0; i < numEntries; i++) {
    const entryOffset = ifdOffset + 2 + i * 12;
    const tag = view.getUint16(entryOffset, littleEndian);
    const type = view.getUint16(entryOffset + 2, littleEndian);
    const count = view.getUint32(entryOffset + 4, littleEndian);

    let value: number;
    if (count <= 4) {
      value = view.getUint32(entryOffset + 8, littleEndian);
    } else {
      value = view.getUint32(entryOffset + 8, littleEndian);
    }

    switch (tag) {
      case 256: width = value; break; // ImageWidth
      case 257: height = value; break; // ImageLength
      case 258: // BitsPerSample
        if (count <= 1) bitsPerSample = value;
        else {
          const offset = count <= 4 ? entryOffset + 8 : value;
          bitsPerSample = view.getUint16(offset, littleEndian);
        }
        break;
      case 259: compression = value; break; // Compression
      case 262: photometricInterpretation = value; break; // PhotometricInterpretation
      case 273: stripOffset = value; break; // StripOffsets
      case 277: samplesPerPixel = value; break; // SamplesPerPixel
      case 279: stripByteCount = value; break; // StripByteCounts
    }
  }

  if (width === 0 || height === 0 || stripOffset === 0) {
    throw new Error("Invalid TIFF: missing required tags");
  }

  if (compression !== 1) {
    throw new Error("TIFF compression not supported (only uncompressed)");
  }

  // Read pixel data
  const bytesPerSample = bitsPerSample / 8;
  const rowSize = width * samplesPerPixel * bytesPerSample;
  const pixels = new PixelBuffer(width, height, "rgba8");

  for (let y = 0; y < height; y++) {
    const rowStart = stripOffset + y * rowSize;
    for (let x = 0; x < width; x++) {
      const srcIdx = rowStart + x * samplesPerPixel * bytesPerSample;
      const dstIdx = (y * width + x) * 4;

      if (bytesPerSample === 1) {
        pixels.data[dstIdx] = bytes[srcIdx]!; // R
        pixels.data[dstIdx + 1] = bytes[srcIdx + 1]!; // G
        pixels.data[dstIdx + 2] = bytes[srcIdx + 2]!; // B
        pixels.data[dstIdx + 3] = samplesPerPixel >= 4 ? bytes[srcIdx + 3]! : 255; // A
      } else {
        // 16-bit — downsample to 8-bit
        const r16 = view.getUint16(srcIdx, littleEndian);
        const g16 = view.getUint16(srcIdx + 2, littleEndian);
        const b16 = view.getUint16(srcIdx + 4, littleEndian);
        const a16 = samplesPerPixel >= 4 ? view.getUint16(srcIdx + 6, littleEndian) : 65535;
        pixels.data[dstIdx] = Math.round(r16 / 257);
        pixels.data[dstIdx + 1] = Math.round(g16 / 257);
        pixels.data[dstIdx + 2] = Math.round(b16 / 257);
        pixels.data[dstIdx + 3] = Math.round(a16 / 257);
      }
    }
  }

  pixels.markDirty();
  return { pixels, width, height };
}

// TIFF writer — basic, no compression. Writes RGB or RGBA.
export function encodeTiff(pixels: PixelBuffer): Blob {
  const w = pixels.width;
  const h = pixels.height;
  const samplesPerPixel = 4; // RGBA
  const bitsPerSample = 8;
  const bytesPerSample = 1;
  const rowSize = w * samplesPerPixel * bytesPerSample;
  const stripByteCount = rowSize * h;

  // TIFF structure: header (8) + IFD count (2) + 13 entries (156) + nextIFD (4) + values + pixel data
  const numEntries = 13;
  const ifdSize = 2 + numEntries * 12 + 4;
  const dataOffset = 8 + ifdSize;

  // Extra space for BitsPerSample values (4 shorts = 8 bytes)
  const valuesOffset = dataOffset;
  const pixelDataOffset = valuesOffset + 8;
  const fileSize = pixelDataOffset + stripByteCount;

  const buffer = new ArrayBuffer(fileSize);
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);
  const littleEndian = true;

  // TIFF header
  view.setUint16(0, 0x4949, littleEndian); // "II" = little endian
  view.setUint16(2, 42, littleEndian); // TIFF magic
  view.setUint32(4, 8, littleEndian); // IFD offset

  // IFD
  const ifdStart = 8;
  view.setUint16(ifdStart, numEntries, littleEndian);

  let entryOffset = ifdStart + 2;
  const writeEntry = (tag: number, type: number, count: number, value: number) => {
    view.setUint16(entryOffset, tag, littleEndian);
    view.setUint16(entryOffset + 2, type, littleEndian);
    view.setUint32(entryOffset + 4, count, littleEndian);
    view.setUint32(entryOffset + 8, value, littleEndian);
    entryOffset += 12;
  };

  writeEntry(256, 3, 1, w); // ImageWidth (SHORT)
  writeEntry(257, 3, 1, h); // ImageLength (SHORT)
  writeEntry(258, 3, samplesPerPixel, valuesOffset); // BitsPerSample (offset to array)
  writeEntry(259, 3, 1, 1); // Compression = none
  writeEntry(262, 3, 1, 2); // PhotometricInterpretation = RGB
  writeEntry(273, 4, 1, pixelDataOffset); // StripOffsets (LONG)
  writeEntry(277, 3, 1, samplesPerPixel); // SamplesPerPixel
  writeEntry(278, 4, 1, h); // RowsPerStrip
  writeEntry(279, 4, 1, stripByteCount); // StripByteCounts
  writeEntry(284, 3, 1, 1); // PlanarConfiguration = chunky
  writeEntry(338, 3, 1, 1); // ExtraSamples = associated alpha
  writeEntry(339, 3, 1, 1); // SampleFormat = unsigned int
  writeEntry(340, 3, samplesPerPixel, valuesOffset); // SampleFormat (offset to array)

  // Next IFD offset = 0
  view.setUint32(entryOffset, 0, littleEndian);

  // BitsPerSample values
  for (let i = 0; i < samplesPerPixel; i++) {
    view.setUint16(valuesOffset + i * 2, bitsPerSample, littleEndian);
  }

  // Pixel data (RGBA, bottom-up for TIFF)
  const data = pixels.data;
  for (let y = 0; y < h; y++) {
    const dstRow = pixelDataOffset + (h - 1 - y) * rowSize;
    for (let x = 0; x < w; x++) {
      const srcIdx = (y * w + x) * 4;
      const dstIdx = dstRow + x * 4;
      bytes[dstIdx] = data[srcIdx]!; // R
      bytes[dstIdx + 1] = data[srcIdx + 1]!; // G
      bytes[dstIdx + 2] = data[srcIdx + 2]!; // B
      bytes[dstIdx + 3] = data[srcIdx + 3]!; // A
    }
  }

  return new Blob([buffer], { type: "image/tiff" });
}

// PDF reader — basic, extracts the first raster image from a simple PDF.
// Only supports PDFs with a single DCTDecode (JPEG) or FlateDecode image.
export async function decodePdf(file: Blob): Promise<{
  pixels: PixelBuffer;
  width: number;
  height: number;
}> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const text = new TextDecoder().decode(bytes);

  // Check PDF header
  if (!text.startsWith("%PDF-")) {
    throw new Error("Not a valid PDF file");
  }

  // Find the first image XObject
  // Look for /Subtype/Image in the PDF
  const imageMarker = "/Subtype/Image";
  let imageOffset = text.indexOf(imageMarker);
  if (imageOffset === -1) {
    // Try to render via browser as fallback
    return decodeImage(file);
  }

  // Find the stream for this image
  const streamStart = text.indexOf("stream\n", imageOffset);
  const streamEnd = text.indexOf("\nendstream", streamStart);
  if (streamStart === -1 || streamEnd === -1) {
    return decodeImage(file);
  }

  // Extract Width and Height
  const objStart = text.lastIndexOf("obj\n", imageOffset);
  const objEnd = text.indexOf("\nendobj", imageOffset);
  if (objStart === -1 || objEnd === -1) {
    return decodeImage(file);
  }

  const objText = text.substring(objStart, objEnd);
  const widthMatch = /\/Width\s+(\d+)/.exec(objText);
  const heightMatch = /\/Height\s+(\d+)/.exec(objText);
  const filterMatch = /\/Filter\s*\/(\w+)/.exec(objText);
  const colorSpaceMatch = /\/ColorSpace\s*\/(\w+)/.exec(objText);

  if (!widthMatch || !heightMatch) {
    return decodeImage(file);
  }

  const width = parseInt(widthMatch[1]!, 10);
  const height = parseInt(heightMatch[1]!, 10);
  const filter = filterMatch ? filterMatch[1] : "Unknown";
  const colorSpace = colorSpaceMatch ? colorSpaceMatch[1] : "DeviceRGB";

  // Extract stream data
  const streamDataStart = streamStart + "stream\n".length;
  const streamData = bytes.subarray(streamDataStart, streamEnd);

  if (filter === "DCTDecode") {
    // JPEG-encoded image — decode via browser
    const jpegBlob = new Blob([streamData], { type: "image/jpeg" });
    return decodeImage(jpegBlob);
  } else if (filter === "FlateDecode") {
    // Try to decompress with DecompressionStream
    try {
      const ds = new DecompressionStream("deflate");
      const writer = ds.writable.getWriter();
      writer.write(streamData);
      writer.close();
      const reader = ds.readable.getReader();
      const chunks: Uint8Array[] = [];
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value) chunks.push(value);
      }
      const totalLen = chunks.reduce((sum, c) => sum + c.length, 0);
      const raw = new Uint8Array(totalLen);
      let offset = 0;
      for (const chunk of chunks) {
        raw.set(chunk, offset);
        offset += chunk.length;
      }

      const samplesPerPixel = colorSpace === "DeviceGray" ? 1 : colorSpace === "DeviceCMYK" ? 4 : 3;
      const bitsPerComponent = 8; // Assume 8-bit
      const pixels = new PixelBuffer(width, height, "rgba8");

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const srcIdx = (y * width + x) * samplesPerPixel;
          const dstIdx = (y * width + x) * 4;

          if (samplesPerPixel === 1) {
            const v = raw[srcIdx] ?? 0;
            pixels.data[dstIdx] = v;
            pixels.data[dstIdx + 1] = v;
            pixels.data[dstIdx + 2] = v;
            pixels.data[dstIdx + 3] = 255;
          } else if (samplesPerPixel === 3) {
            pixels.data[dstIdx] = raw[srcIdx] ?? 0;
            pixels.data[dstIdx + 1] = raw[srcIdx + 1] ?? 0;
            pixels.data[dstIdx + 2] = raw[srcIdx + 2] ?? 0;
            pixels.data[dstIdx + 3] = 255;
          } else if (samplesPerPixel === 4) {
            // CMYK to RGB conversion
            const c = raw[srcIdx] ?? 0;
            const m = raw[srcIdx + 1] ?? 0;
            const y = raw[srcIdx + 2] ?? 0;
            const k = raw[srcIdx + 3] ?? 0;
            const cn = c / 255;
            const mn = m / 255;
            const yn = y / 255;
            const kn = k / 255;
            pixels.data[dstIdx] = Math.round(255 * (1 - cn) * (1 - kn));
            pixels.data[dstIdx + 1] = Math.round(255 * (1 - mn) * (1 - kn));
            pixels.data[dstIdx + 2] = Math.round(255 * (1 - yn) * (1 - kn));
            pixels.data[dstIdx + 3] = 255;
          }
        }
      }

      pixels.markDirty();
      return { pixels, width, height };
    } catch {
      return decodeImage(file);
    }
  } else {
    // Unsupported filter — fall back to browser decode
    return decodeImage(file);
  }
}
